import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { leadsService } from '../../services/leads.service'
import { channelsService } from '../../services/channels.service'
import { telegramService } from '../../services/telegram.service'
import api from '../../services/api'

interface Subscription {
  id: number
  plan: string
  status: string
  current_period_end: string
  trial_ends_at: string | null
}

export default function Dashboard() {
  const { user } = useAuthStore()

  // Fetch lead stats
  const { data: leadStats } = useQuery({
    queryKey: ['leadStats'],
    queryFn: () => leadsService.getStats(),
  })

  // Fetch channel stats
  const { data: channelStats } = useQuery({
    queryKey: ['channelStats'],
    queryFn: () => channelsService.getStats(),
  })

  // Fetch telegram status
  const { data: telegramStatus } = useQuery({
    queryKey: ['telegramStatus'],
    queryFn: () => telegramService.getStatus(),
  })

  // Fetch subscription
  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await api.get<Subscription>('/subscriptions/current')
      return response.data
    },
  })

  // Calculate reply rate
  const replyRate = leadStats && leadStats.sent > 0
    ? Math.round((leadStats.replied / leadStats.sent) * 100)
    : 0

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const planLabels: Record<string, string> = {
    free: 'Бесплатный',
    trial: 'Пробный период',
    starter: 'Стартовый',
    professional: 'Профессиональный',
    enterprise: 'Корпоративный',
  }

  return (
    <div className="pt-14">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Добро пожаловать, {user?.email}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Всего лидов"
          value={leadStats?.total_leads?.toString() || '0'}
          change={`+${leadStats?.today_leads || 0} сегодня`}
          positive={(leadStats?.today_leads || 0) > 0}
          icon="👥"
        />
        <StatCard
          title="Отправлено сообщений"
          value={leadStats?.sent?.toString() || '0'}
          change={`${leadStats?.replied || 0} ответов`}
          positive={(leadStats?.replied || 0) > 0}
          icon="📨"
        />
        <StatCard
          title="Конверсия"
          value={`${replyRate}%`}
          change="ответили на сообщения"
          positive={replyRate > 0}
          icon="📈"
        />
        <StatCard
          title="Активных каналов"
          value={channelStats?.active_channels?.toString() || '0'}
          change={`из ${channelStats?.total_channels || 0}`}
          positive={(channelStats?.active_channels || 0) > 0}
          icon="📢"
        />
      </div>

      {/* Telegram Status & Subscription */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Telegram Status */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Статус Telegram
          </h2>
          {telegramStatus?.connected ? (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-lg">✓</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">Подключен</p>
                <p className="text-sm text-gray-500">
                  {telegramStatus.phone_number || 'Аккаунт активен'}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 mb-3">
                Telegram не подключен. Подключите аккаунт для мониторинга каналов.
              </p>
              <Link to="/settings" className="btn-primary inline-block">
                Подключить Telegram
              </Link>
            </div>
          )}
        </div>

        {/* Subscription Widget */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Подписка
          </h2>
          {subscription ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-medium text-gray-900">
                  {planLabels[subscription.plan] || subscription.plan}
                </span>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    subscription.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : subscription.status === 'trialing'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {subscription.status === 'active'
                    ? 'Активна'
                    : subscription.status === 'trialing'
                    ? 'Пробный'
                    : 'Неактивна'}
                </span>
              </div>
              {subscription.trial_ends_at && (
                <p className="text-sm text-blue-600">
                  Пробный период до: {formatDate(subscription.trial_ends_at)}
                </p>
              )}
              {subscription.status !== 'trialing' && subscription.current_period_end && (
                <p className="text-sm text-gray-500">
                  Следующее списание: {formatDate(subscription.current_period_end)}
                </p>
              )}
              {subscription.plan === 'free' || subscription.plan === 'trial' ? (
                <Link
                  to="/settings"
                  className="mt-4 inline-block text-blue-600 hover:text-blue-800 text-sm"
                >
                  Улучшить план →
                </Link>
              ) : null}
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800">
                Нет активной подписки
              </p>
              <Link to="/settings" className="btn-primary mt-4 inline-block">
                Выбрать план
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Lead Pipeline */}
      {leadStats && (leadStats.total_leads > 0 || leadStats.pending_review > 0) && (
        <div className="card mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Воронка лидов
          </h2>
          <div className="flex items-center justify-between">
            <PipelineStep
              label="На рассмотрении"
              value={leadStats.pending_review}
              color="yellow"
            />
            <PipelineArrow />
            <PipelineStep
              label="Одобрено"
              value={leadStats.approved}
              color="green"
            />
            <PipelineArrow />
            <PipelineStep
              label="Сообщения готовы"
              value={leadStats.message_generated}
              color="blue"
            />
            <PipelineArrow />
            <PipelineStep
              label="Отправлено"
              value={leadStats.sent}
              color="purple"
            />
            <PipelineArrow />
            <PipelineStep
              label="Ответили"
              value={leadStats.replied}
              color="emerald"
            />
          </div>
          {leadStats.pending_review > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Link to="/leads" className="text-blue-600 hover:text-blue-800 text-sm">
                {leadStats.pending_review} лидов ожидают рассмотрения →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Быстрые действия
          </h2>
          <div className="space-y-3">
            {!telegramStatus?.connected && (
              <QuickAction
                title="Подключить Telegram"
                description="Подключите аккаунт для начала работы"
                href="/settings"
                icon="📱"
                highlight
              />
            )}
            <QuickAction
              title="Добавить канал"
              description="Добавьте каналы для мониторинга"
              href="/channels"
              icon="📢"
            />
            <QuickAction
              title="Просмотреть лидов"
              description="Управляйте найденными лидами"
              href="/leads"
              icon="👥"
            />
            <QuickAction
              title="Настройки"
              description="Настройте уведомления и профиль"
              href="/settings"
              icon="⚙️"
            />
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Последняя активность
          </h2>
          {leadStats && leadStats.today_leads > 0 ? (
            <div className="space-y-3">
              <ActivityItem
                icon="🆕"
                text={`${leadStats.today_leads} новых лидов сегодня`}
                time="Сегодня"
              />
              {leadStats.sent > 0 && (
                <ActivityItem
                  icon="📨"
                  text={`${leadStats.sent} сообщений отправлено`}
                  time="Всего"
                />
              )}
              {leadStats.replied > 0 && (
                <ActivityItem
                  icon="💬"
                  text={`${leadStats.replied} ответов получено`}
                  time="Всего"
                />
              )}
            </div>
          ) : (
            <div className="text-gray-500 text-center py-8">
              Пока нет активности. Добавьте каналы для начала мониторинга.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StatCard({
  title,
  value,
  change,
  positive,
  icon,
}: {
  title: string
  value: string
  change: string
  positive: boolean
  icon: string
}) {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-gray-600">{title}</p>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p
        className={`text-sm mt-1 ${
          positive ? 'text-green-600' : 'text-gray-500'
        }`}
      >
        {change}
      </p>
    </div>
  )
}

function QuickAction({
  title,
  description,
  href,
  icon,
  highlight,
}: {
  title: string
  description: string
  href: string
  icon: string
  highlight?: boolean
}) {
  return (
    <Link
      to={href}
      className={`block p-3 rounded-lg transition-colors ${
        highlight
          ? 'bg-blue-50 border border-blue-200 hover:bg-blue-100'
          : 'border border-gray-200 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <span className="text-xl">{icon}</span>
        <div>
          <p className={`font-medium ${highlight ? 'text-blue-900' : 'text-gray-900'}`}>
            {title}
          </p>
          <p className={`text-sm ${highlight ? 'text-blue-700' : 'text-gray-600'}`}>
            {description}
          </p>
        </div>
      </div>
    </Link>
  )
}

function PipelineStep({
  label,
  value,
  color,
}: {
  label: string
  value: number
  color: string
}) {
  const colorClasses: Record<string, string> = {
    yellow: 'bg-yellow-100 text-yellow-800',
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    purple: 'bg-purple-100 text-purple-800',
    emerald: 'bg-emerald-100 text-emerald-800',
  }

  return (
    <div className="text-center">
      <div
        className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${colorClasses[color]}`}
      >
        <span className="text-lg font-bold">{value}</span>
      </div>
      <p className="text-xs text-gray-500 mt-2">{label}</p>
    </div>
  )
}

function PipelineArrow() {
  return (
    <div className="text-gray-300">
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  )
}

function ActivityItem({
  icon,
  text,
  time,
}: {
  icon: string
  text: string
  time: string
}) {
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="text-lg">{icon}</span>
      <div className="flex-1">
        <p className="text-sm text-gray-900">{text}</p>
      </div>
      <span className="text-xs text-gray-500">{time}</span>
    </div>
  )
}
